import { useCallback } from 'react';

import type { AccountEntity } from '@actual-app/core/types/models';

import {
  useSyncAndDownloadMutation,
  useUnlinkAccountMutation,
} from '#accounts';
import { authorizeBank } from '#enablebanking';
import { useEnableBankingStatus } from '#hooks/useEnableBankingStatus';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';
import { registrarUso } from '#uso';

/**
 * Conectar con el banco desde Cuentas con el flujo real de Actual (Enable
 * Banking): sin configurar, su modal de Application ID + .pem; configurado,
 * elegir banco → autorizar en el banco → volver → enlazar las cuentas
 * (a una existente o nueva). Sincronizar y desconectar usan sus acciones.
 * En la beta, «Conectar banco» solo se ofrece cuando el servidor lo permite
 * (`permitida`).
 */
export function useBanco() {
  const dispatch = useDispatch();
  const { configuredEnableBanking, bancaPermitida, isLoading } =
    useEnableBankingStatus();
  const syncAndDownload = useSyncAndDownloadMutation();
  const unlink = useUnlinkAccountMutation();

  const conectar = useCallback(
    (cuentaId?: AccountEntity['id']) => {
      // Rumbo (beta): sin banca permitida no se ofrece ni se abre nada.
      if (!bancaPermitida) {
        return;
      }
      const elegirBanco = () => void authorizeBank(dispatch, cuentaId);
      if (configuredEnableBanking) {
        elegirBanco();
        return;
      }
      dispatch(
        pushModal({
          modal: {
            name: 'enablebanking-init',
            options: {
              // El modal se cierra justo después de `onSuccess`: el de elegir
              // banco se abre cuando ya se ha ido.
              onSuccess: () => window.setTimeout(elegirBanco, 200),
            },
          },
        }),
      );
    },
    [bancaPermitida, configuredEnableBanking, dispatch],
  );

  const sincronizar = useCallback(
    (cuentaId?: AccountEntity['id']) => {
      registrarUso('banco_sincronizar', {
        origen: 'manual',
        tipo: cuentaId ? 'cuenta' : 'todas',
      });
      syncAndDownload.mutate({ id: cuentaId });
    },
    [syncAndDownload],
  );

  const desconectar = useCallback(
    (cuentaId: AccountEntity['id']) => unlink.mutate({ id: cuentaId }),
    [unlink],
  );

  return {
    /** Si se puede ofrecer «Conectar banco» (en la beta, solo al admin). */
    permitida: bancaPermitida,
    /** Aún no se sabe si hay banca (primera pregunta al servidor). */
    cargando: isLoading,
    conectar,
    sincronizar,
    desconectar,
    sincronizando: syncAndDownload.isPending,
  };
}
